import { memo } from "react";

// Advisors Sections
import FollowUpPerformanceComponent from "@PayPlans/components/Defaults/Advisors/FollowUpPerformance";

// Common Sections
import CallActivityPerformanceComponent from "@PayPlans/components/Commons/CallActivityPerformance";
import CustomerSatisfactionComponent from "@PayPlans/components/Commons/CustomerSatisfaction";
import ElrHroExtension from "@/PayPlans/components/Commons/ElrHroExtension";
import LongevityComponent from "@PayPlans/components/Commons/Longevity";
import TopPerformerBonus from "@PayPlans/components/Commons/TopPerformer";
import RetentionPayComponent from "@/PayPlans/components/Defaults/Advisors/AdvisorsRetentionPay";
import HolidayPayComponent from "@/PayPlans/components/Defaults/Technicians/HolidayPay";

// Specific Sections
import DefaultCommissionsComponent from "@/PayPlans/components/Defaults/Advisors/DefaultCommissions";

// Props
import {
	IDefaultCommissionsSectionProps,
	IFollowUpPerformanceSectionProps,
	TCallActivityPerformanceSectionProps,
	TCustomerSatisfactionSectionProps,
	TLongevitySectionProps,
	TTopPerformerSectionProps,
	IElrHroBonusSectionProps,
	HolidayPaySectionProps,
} from "@/PayPlans/types/sections";

import { TAdvisorsRetentionPayProps } from "@/PayPlans/components/Defaults/Advisors/AdvisorsRetentionPay/types";

function FollowUpPerformanceSection(props: Readonly<IFollowUpPerformanceSectionProps>) {
	return <FollowUpPerformanceComponent {...props} />;
}

function CallActivityPerformanceSection(props: Readonly<TCallActivityPerformanceSectionProps>) {
	return <CallActivityPerformanceComponent {...props} sectionId={"callActivityPerformance"} />;
}

function CustomerSatisfactionSection(props: Readonly<TCustomerSatisfactionSectionProps>) {
	const {
		sectionId = "customerSatisfaction",
		content = {
			makeMetrics: [],
			weighting: {
				weightingType: "UNITS",
				customWeights: [],
			},
		},
		excludesMakes = ["Cadillac"],
		errors = [],
		isService = false,
		useOnlyIndividualMetrics = false,
		useOnlyStoreMetrics = false,
	} = props;
	return (
		<CustomerSatisfactionComponent
			{...props}
			sectionId={sectionId}
			content={content}
			excludesMakes={excludesMakes}
			errors={errors}
			isService={isService}
			useOnlyIndividualMetrics={useOnlyIndividualMetrics}
			useOnlyStoreMetrics={useOnlyStoreMetrics}
		/>
	);
}

function DefaultCommissionsSection(props: Readonly<IDefaultCommissionsSectionProps>) {
	const {
		sectionId = "default",
		content = null,
		errors = [],
		isPartsAndLabor = false,
		isGuaranteedDraw = false,
		loading = false,
		readOnly = false,
		useSectionsErrors = () => {},
		onActionUpdate = () => {},
	} = props;

	return (
		<DefaultCommissionsComponent
			sectionId={sectionId}
			content={content}
			tabsProps={{
				tabs: ["Parts", "Labor", "Parts & Labor"],
				errors,
				isPartsAndLabor,
				isGuaranteedDraw,
			}}
			loading={loading}
			readOnly={readOnly}
			useSectionsErrors={useSectionsErrors}
			onActionUpdate={onActionUpdate}
		/>
	);
}

function LongevitySection(props: Readonly<TLongevitySectionProps>) {
	const { sectionId = "longevity", content = [] } = props;
	return <LongevityComponent {...props} sectionId={sectionId} content={content} />;
}

const TopAdvisorsBonusSection = (props: Readonly<TTopPerformerSectionProps>) => (
	<TopPerformerBonus {...props} sectionId={"topAdvisorsBonus"} />
);
function RetentionPaySection(props: Readonly<TAdvisorsRetentionPayProps>) {
	return <RetentionPayComponent {...props} />;
}

function ElrHroBonusSection(props: Readonly<IElrHroBonusSectionProps>) {
	return <ElrHroExtension {...props} />;
}

function HolidayPaySection(props: Readonly<HolidayPaySectionProps>) {
	return <HolidayPayComponent {...props} />;
}

const CallActivityPerformance = memo(CallActivityPerformanceSection);
const CustomerSatisfaction = memo(CustomerSatisfactionSection);
const DefaultCommissions = memo(DefaultCommissionsSection);
const Longevity = memo(LongevitySection);
const RetentionPay = memo(RetentionPaySection);
const TopAdvisorsBonus = memo(TopAdvisorsBonusSection);
const FollowUpPerformance = memo(FollowUpPerformanceSection);
const ElrHro = memo(ElrHroBonusSection);
const HolidayPay = memo(HolidayPaySection);

export {
	FollowUpPerformance,
	CallActivityPerformance,
	CustomerSatisfaction,
	DefaultCommissions,
	Longevity,
	RetentionPay,
	TopAdvisorsBonus,
	ElrHro,
	HolidayPay,
};
